import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { useAuth } from '../Components/AuthContext';
import { useParams } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Button } from '../Components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetBody, SheetFooter } from '../Components/ui/sheet';

const AVAILABLE_ROLES = ['UMS_READ', 'UMS_WRITE', 'ADMIN', 'UMS_DELETE'];

const EMPTY_FORM = {
  email: '',
  firstName: '',
  lastName: '',
  active: true,
  userRoles: [],
};

const UserTable = () => {
  const [users, setUsers] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState('create'); // 'create' | 'edit'
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingUserId, setEditingUserId] = useState(null);

  const { tenantDomain } = useParams();
  const { updateTenantDomain, user } = useAuth();

  useEffect(() => {
    updateTenantDomain(tenantDomain);
  }, [tenantDomain]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/list-users`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const parseUser = () => {
    try {
      return typeof user === 'string' ? JSON.parse(user) : user;
    } catch { return null; }
  };

  const hasDeletePermission = () => {
    const userObj = parseUser();
    return userObj?.roles?.includes('ADMIN') && userObj?.roles?.includes('UMS_DELETE');
  };

  const hasWritePermission = () => {
    const userObj = parseUser();
    return userObj?.roles?.includes('UMS_WRITE') || userObj?.roles?.includes('ADMIN');
  };

  const openCreateSheet = () => {
    setSheetMode('create');
    setFormData(EMPTY_FORM);
    setEditingUserId(null);
    setSheetOpen(true);
  };

  const openEditSheet = (user) => {
    setSheetMode('edit');
    setFormData({ ...user, userRoles: [...user.userRoles] });
    setEditingUserId(user.userId);
    setSheetOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleRoleChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      userRoles: checked
        ? [...prev.userRoles, name]
        : prev.userRoles.filter(r => r !== name),
    }));
  };

  const handleSubmit = async () => {
    try {
      const url = sheetMode === 'create'
        ? `${process.env.REACT_APP_API_URL}/${tenantDomain}/user`
        : `${process.env.REACT_APP_API_URL}/${tenantDomain}/user/${editingUserId}`;
      const method = sheetMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await fetchUsers();
        setSheetOpen(false);
      } else {
        console.error('Error saving user:', await response.text());
      }
    } catch (error) {
      console.error('Error saving user:', error);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/${tenantDomain}/user/${userId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (response.ok) await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-muted-foreground">
          {users.length} {users.length === 1 ? 'user' : 'users'}
        </h2>
        {hasWritePermission() && (
          <Button onClick={openCreateSheet}>
            <Plus size={16} className="mr-1.5" />
            Add User
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">First Name</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Last Name</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Roles</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => (
              <tr
                key={u.userId}
                className={cn("border-b last:border-0 hover:bg-muted/30 transition-colors", i % 2 === 0 ? "" : "bg-muted/10")}
              >
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">{u.firstName}</td>
                <td className="px-4 py-3">{u.lastName}</td>
                <td className="px-4 py-3">
                  <span className={cn(
                    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                    u.active
                      ? "bg-green-100 text-green-700"
                      : "bg-muted text-muted-foreground"
                  )}>
                    {u.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-0.5">
                    {u.userRoles?.map(role => (
                      <span key={role} className="text-xs text-muted-foreground">{role}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {hasWritePermission() && (
                      <button
                        onClick={() => openEditSheet(u)}
                        className="p-1.5 rounded hover:bg-accent transition-colors"
                        title="Edit"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    {hasDeletePermission() && (
                      <button
                        onClick={() => handleDelete(u.userId)}
                        className="p-1.5 rounded hover:bg-destructive/10 text-destructive transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{sheetMode === 'create' ? 'Add User' : 'Edit User'}</SheetTitle>
          </SheetHeader>
          <SheetBody>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-input"
                />
                <label htmlFor="active" className="text-sm font-medium">Active</label>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium">Roles</label>
                {AVAILABLE_ROLES.map(role => (
                  <div key={role} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`role-${role}`}
                      name={role}
                      checked={formData.userRoles?.includes(role)}
                      onChange={handleRoleChange}
                      className="h-4 w-4 rounded border-input"
                    />
                    <label htmlFor={`role-${role}`} className="text-sm">{role}</label>
                  </div>
                ))}
              </div>
            </div>
          </SheetBody>
          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit}>
              {sheetMode === 'create' ? 'Create User' : 'Save Changes'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default UserTable;
